import { create } from 'zustand';

import { ValueLists, notification } from '@ohpen/enso-ui';
import { FieldType, optionsGetAllOptionsForFields } from '@pmp/api';
import { OPERATION_ORDER, getApiErrorMessage } from '@pmp/utils';

const FIELD_KEY_ORDER: Partial<Record<FieldType, string[]>> = {
  [FieldType.OPERATION_TYPE]: OPERATION_ORDER,
};

const applyFieldKeyOrder = (fields: ValueLists): ValueLists =>
  Object.fromEntries(
    Object.entries(fields).map(([field, list]) => {
      const order = FIELD_KEY_ORDER[field as FieldType];
      if (!order || !list) return [field, list];
      const rank = (key: string) => {
        const i = order.indexOf(key);
        return i === -1 ? order.length : i;
      };
      const keys = Object.keys(list).sort((a, b) => rank(a) - rank(b));
      return [field, Object.fromEntries(keys.map(k => [k, list[k]]))];
    }),
  );

export type GetValueListsProps = {
  fetchIfMissing?: FieldType[];
  alwaysFetch?: FieldType[];
};

type ValueListsStore = {
  valueLists: ValueLists;
  isLoadingValueLists: boolean;
  getValueLists: (params?: GetValueListsProps) => Promise<void>;
};

export const useValueListsStore = create<ValueListsStore>((set, get) => {
  return {
    valueLists: {},
    isLoadingValueLists: false,

    getValueLists: async (params?: GetValueListsProps) => {
      const always = params?.alwaysFetch ?? [];
      const missing = (params?.fetchIfMissing ?? []).filter(name => !get().valueLists[name]);
      const listsToFetch = Array.from(new Set([...always, ...missing]));
      if (!listsToFetch.length) return;

      set({ isLoadingValueLists: true });
      try {
        const {
          data: { fields },
        } = await optionsGetAllOptionsForFields({ query: { fields: listsToFetch } });
        if (fields) {
          set(state => ({ valueLists: { ...state.valueLists, ...applyFieldKeyOrder(fields) } }));
        }
      } catch (error) {
        // Callers fire this from effects without awaiting, so a rethrow would become an unhandled
        // rejection. Report here and leave the already-cached lists untouched.
        notification().error({ title: getApiErrorMessage(error) });
      } finally {
        set({ isLoadingValueLists: false });
      }
    },
  };
});
